import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Link as RouterLink } from 'react-router-dom';
import type { GuideProfile } from '@/types';
import { getLowestGuidePrice, formatGuidePrice } from '@/features/guide/utils/pricing';
import { getGuideProfilePath } from '@/features/guides/utils/guideBookPath';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80';

function getPricePerHour(pricing: GuideProfile['pricing']): string | null {
  if (!pricing) return null;
  const lowest = getLowestGuidePrice(pricing);
  if (lowest == null) return null;
  return formatGuidePrice(lowest.amount);
}

function getResponseRate(guide: GuideProfile): number {
  const rating = guide.rating ?? 4.6;
  const reviewWeight = Math.min(guide.reviewCount ?? 0, 200) / 200;
  return Math.round(Math.min(99, 90 + rating + reviewWeight * 4));
}

interface GuideListingCardProps {
  guide: GuideProfile;
  onBookNow: (guideId: string) => void;
  travelDate?: string;
  city?: string;
  travelers?: number;
}

export function GuideListingCard({
  guide,
  onBookNow,
  travelDate,
  city,
  travelers,
}: GuideListingCardProps) {
  const priceStr = getPricePerHour(guide.pricing);
  const availableOnDate = guide.availableOnTravelDate !== false;
  const canBook = getLowestGuidePrice(guide.pricing) != null && availableOnDate;
  const hasReviews = (guide.reviewCount ?? 0) > 0;
  const specs = guide.specializations ?? [];
  const visibleSpecs = specs.slice(0, 3);
  const extraSpecs = specs.length > 3 ? specs.length - 3 : 0;
  const responseRate = getResponseRate(guide);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: 'transparent',
          boxShadow: '0 20px 40px -8px rgba(15, 23, 42, 0.12)',
          transform: 'translateY(-4px)',
          '& .guide-image': {
            transform: 'scale(1.05)',
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '4/3',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          className="guide-image"
          component="img"
          src={guide.profilePhoto || FALLBACK_IMAGE}
          alt={guide.fullName ?? 'Guide'}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(15, 23, 42, 0.4) 0%, transparent 50%)',
          }}
        />

        <Chip
          icon={<VerifiedIcon sx={{ fontSize: '14px !important' }} />}
          label="Verified"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            color: 'primary.main',
            fontWeight: 600,
            fontSize: '0.6875rem',
            height: 26,
            backdropFilter: 'blur(8px)',
            '& .MuiChip-icon': {
              color: 'primary.main',
            },
          }}
        />

        {!availableOnDate && (
          <Chip
            label="Unavailable on date"
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              bgcolor: 'rgba(239, 68, 68, 0.92)',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.6875rem',
              height: 26,
            }}
          />
        )}

        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            transition: 'all 200ms ease',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)',
              '& svg': { color: 'error.main' },
            },
          }}
          aria-label="Save guide"
        >
          <FavoriteBorderIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </IconButton>

        {hasReviews && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 1.5,
              backdropFilter: 'blur(8px)',
            }}
          >
            <StarIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {(guide.rating ?? 0).toFixed(1)}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              ({guide.reviewCount})
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: 2.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: '1.0625rem',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {guide.fullName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 14, color: 'text.muted' }} />
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
              }}
            >
              {guide.city}
              {guide.state && `, ${guide.state}`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {hasReviews && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'text.muted' }} />
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                }}
              >
                {responseRate}% response
              </Typography>
            </Box>
          )}
          {hasReviews && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
              }}
            >
              {guide.reviewCount} tours
            </Typography>
          )}
        </Box>

        {specs.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {visibleSpecs.map((spec) => (
              <Chip
                key={spec}
                label={spec}
                size="small"
                sx={{
                  fontSize: '0.6875rem',
                  height: 24,
                  bgcolor: 'background.default',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              />
            ))}
            {extraSpecs > 0 && (
              <Chip
                label={`+${extraSpecs}`}
                size="small"
                sx={{
                  fontSize: '0.6875rem',
                  height: 24,
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 'auto',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {priceStr ? (
            <Box>
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                {priceStr}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    color: 'text.secondary',
                    ml: 0.25,
                  }}
                >
                  /hr
                </Typography>
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.muted',
                }}
              >
                Starting price
              </Typography>
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.muted',
                fontStyle: 'italic',
              }}
            >
              Price not set
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={RouterLink}
              to={getGuideProfilePath(guide._id, travelDate, city, travelers)}
              variant="outlined"
              size="small"
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                px: 2,
                py: 0.875,
                borderRadius: 2,
                fontSize: '0.8125rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: 'primary.50',
                },
              }}
            >
              View
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!canBook}
              onClick={() => onBookNow(guide._id)}
              sx={{
                bgcolor: 'primary.main',
                px: 2,
                py: 0.875,
                borderRadius: 2,
                fontSize: '0.8125rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                },
              }}
            >
              {canBook ? 'Book Now' : !availableOnDate ? 'Unavailable' : 'Book Now'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
